import { RouteLocationNormalized } from 'vue-router';
import { TENANT_SUBSCRIPTION_STATUS } from '~/app/auth/enums/TenantSubscriptionStatus';
import { LOGIN_REDIRECT_QUERY_KEY, buildLoginUrl, parseLoginRedirect } from '~/app/auth/support/loginRedirect';

const getBaseHomepathUrl = () => {
    const { authed } = useAuthStore();
    if (!authed!.hasPermission('app:dashboard')) {
        const { appsModules } = useModules();
        for (const possibleModule of appsModules.value) {
            const permissionKey = possibleModule.permissionKey ?? `${possibleModule.key}:access`;
            if (authed!.hasPermission(permissionKey)) {
                if (possibleModule.key === 'invoicing' && !authed!.hasPermission('invoicing:invoicing_statistics')) {
                    return `/${possibleModule.key}/contacts`;
                }
                return possibleModule.basePath ?? `/${possibleModule.key}/${possibleModule.defaultPath()}`;
            }
        }
    }

    const runtimeConfig = useRuntimeConfig();
    return `/${runtimeConfig.public.homepage}`;
};

export default (route: RouteLocationNormalized): string | null => {
    const { usableToken, authed, tenant } = useAuthStore();
    const complete = usableToken && authed && tenant;
    const loadingAuthPage = route.path.startsWith('/auth');
    // Covers both the new inbound flow (/auth/register) and the legacy tenant page so a connected
    // user without a tenant is not bounced off the registration flow in a loop.
    const loadingRegistrationPage = route.path.startsWith('/auth/register');
    const loadingImpersonationPage = route.path.startsWith('/auth/impersonate');
    const loadingTenantInaccessiblePage = route.path.startsWith('/auth/tenant');

    const getHomepageUrl = () => {
        const { query } = useRoute();
        const params = new URLSearchParams(query);
        return `${getBaseHomepathUrl()}?${params.toString()}`;
    };

    /**
     * If we load this page, we do not care about the setup.
     * This might happen after a login, or after the initial loading.
     */
    if (loadingTenantInaccessiblePage) {
        if (complete && route.name === 'auth-tenant-id-cancelled' && tenant.subscriptionStatus !== TENANT_SUBSCRIPTION_STATUS.CANCELLED) {
            return getHomepageUrl();
        }

        // Pause over: back home — the unpaid experience matching the tenant's level
        // takes over from there (banners or level 4 confinement, TDD-5605).
        if (complete && route.name === 'auth-tenant-id-paused' && tenant.subscriptionStatus !== TENANT_SUBSCRIPTION_STATUS.PAUSED) {
            return getHomepageUrl();
        }

        return null;
    }

    /**
     * Everything is already loaded, but the user tries to access the login, register or register tenant pages.
     * In this case, they will be redirected to the homepage.
     */
    if (complete && loadingImpersonationPage) {
        return null;
    }

    if (complete && loadingAuthPage) {
        return parseLoginRedirect(route.query[LOGIN_REDIRECT_QUERY_KEY]) ?? getHomepageUrl();
    }

    /**
     * User is unauthenticated but trying to impersonate.
     *
     * Redirect to login page, even when the link carries a token: every impersonated request
     * also sends the operator's own session as X-Impersonating-Token, and the API answers 403 without it.
     */
    if (!authed && loadingImpersonationPage) {
        return '/auth/login';
    }

    /**
     * The user has no token yet, or has a token but the user isn't retrieved.
     * In this case, they will be redirected to the login page, unless they are already on an auth page.
     */
    if ((!usableToken || !authed) && !loadingAuthPage) {
        return buildLoginUrl(route.fullPath);
    }

    /**
     * The user is logged in, but has no tenant yet.
     * They need to register their tenant (company) first before using any other features.
     * Exception: a cancelled/inactive tenant stores its inaccessible path — redirect there instead.
     */
    if (authed && !tenant && !loadingRegistrationPage && !route.path.startsWith('/switch-tenant')) {
        const { inaccessibleTenantPath } = useAuthStore();
        if (inaccessibleTenantPath) return inaccessibleTenantPath;
        return '/auth/register';
    }

    /**
     * Unpaid level 4 confinement (TDD-5603) on initial load: during bootstrap the
     * unpaid-blocked middleware runs before the tenant meta is fetched, so the
     * bootstrap's final redirection pass (and every navigation once setup is
     * complete) also funnels a blocked tenant to the invoices screen.
     */
    if (complete) {
        const { isBlocked } = useUnpaidStatus();
        if (isBlocked.value && !isUnpaidConfinementExempt(route.path)) {
            // Every caller navigates to the returned path.
            useUnpaidTracking().setInvoicesSource('blocked_redirect');
            return '/settings/invoices';
        }
    }

    if (route.path === '/') {
        return getHomepageUrl();
    }

    return getSettingsRedirection(route);
};

const getSettingsRedirection = (route: RouteLocationNormalized): string | null => {
    if (!route.path.startsWith('/settings')) {
        return null;
    }

    const { authed } = useAuthStore();
    if (!authed) {
        return null;
    }

    if (authed.hasPermission('settings:access')) {
        return null;
    }

    if (authed.role === 'super-admin') {
        return null;
    }

    return getBaseHomepathUrl();
};
